import { useState } from 'react';
import * as bd from 'react-basic-design';
import {
    UserLicenseDTO,
    useUpdateDownloadLinkApi,
    useResetDownloadCountApi,
    useUpdateLicenseStatusApi,
    useResolveBrokenLicenseApi,
} from '../../../api/user-license-api';
import SvgClose from '../../../assets/icons/Close';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';

interface ManageUserLicenseModalProps {
    show: boolean;
    selectedLicense?: UserLicenseDTO;
    onHide: () => void;
    onUpdate: () => void;
}

export function ManageUserLicenseModal({
    show,
    selectedLicense,
    onHide,
    onUpdate,
}: ManageUserLicenseModalProps) {
    const [downloadLink, setDownloadLink] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const updateLinkApi = useUpdateDownloadLinkApi();
    const resetDownloadApi = useResetDownloadCountApi();
    const updateStatusApi = useUpdateLicenseStatusApi();
    const resolveBrokenApi = useResolveBrokenLicenseApi();

    const handleUpdateLink = () => {
        if (!selectedLicense || !downloadLink.trim()) return;

        updateLinkApi.post(
            null,
            {
                licenseId: selectedLicense.id,
                downloadLink: downloadLink.trim(),
            },
            () => {
                onUpdate();
                notify.success(translate('download-link-updated-successfully'));
            },
            notify.error
        );
    };

    const handleResetDownloadCount = () => {
        if (!selectedLicense) return;

        bd.msgbox(
            translate('reset-download-count'),
            translate('confirm-reset-download-count'),
            [
                {
                    title: translate('reset'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        resetDownloadApi.post(
                            { licenseId: selectedLicense.id! },
                            null,
                            () => {
                                onUpdate();
                                notify.success(translate('download-count-reset-successfully'));
                                hide();
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleUpdateStatus = (status: string) => {
        if (!selectedLicense) return;

        updateStatusApi.post(
            null,
            {
                licenseId: selectedLicense.id,
                status: status,
            },
            () => {
                onUpdate();
                setNewStatus(status);
                notify.success(translate('status-updated-successfully'));
            },
            notify.error
        );
    };

    const handleResolveBroken = () => {
        if (!selectedLicense || !selectedLicense.isBroken) return;

        bd.msgbox(
            translate('resolve-broken-license'),
            translate('confirm-resolve-broken-license'),
            [
                {
                    title: translate('resolve'),
                    variant: 'contained',
                    color: 'primary',
                    action: hide => {
                        resolveBrokenApi.post(
                            { licenseId: selectedLicense.id! },
                            null,
                            () => {
                                onUpdate();
                                notify.success(translate('broken-license-resolved-successfully'));
                                hide();
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    if (!selectedLicense) return null;

    return (
        <BasicModal
            size="lg"
            show={show}
            onHide={onHide}
            title={
                <div>
                    <T>license-details</T>
                    <span className="text-muted ms-2">#{selectedLicense.id}</span>
                </div>
            }
            headerButtons={
                <bd.Button variant="outline" size="sm" icon={<SvgClose />} onClick={onHide}>
                    <T>close</T>
                </bd.Button>
            }
        >
            <div className="p-3">
                {/* License Info */}
                <bd.Paper className="mb-3 p-3">
                    <bd.Flex gap={3} wrap>
                        <div style={{ flex: '1 1 300px' }}>
                            <div className="mb-2">
                                <strong><T>user</T>:</strong> {selectedLicense.userName}
                            </div>
                            <div className="mb-2">
                                <strong><T>product</T>:</strong> {selectedLicense.productName}
                            </div>
                            <div className="mb-2">
                                <strong><T>license-code</T>:</strong>{' '}
                                <code>{selectedLicense.licenseCode}</code>
                            </div>
                        </div>
                        <div style={{ flex: '1 1 300px' }}>
                            <div className="mb-2">
                                <strong><T>activated-at</T>:</strong>{' '}
                                {selectedLicense.activatedAt
                                    ? new Date(selectedLicense.activatedAt).toLocaleString()
                                    : '-'}
                            </div>
                            <div className="mb-2">
                                <strong><T>expires-at</T>:</strong>{' '}
                                {selectedLicense.expiresAt
                                    ? new Date(selectedLicense.expiresAt).toLocaleString()
                                    : <T>never</T>}
                            </div>
                            <div className="mb-2">
                                <strong><T>downloads</T>:</strong>{' '}
                                {selectedLicense.downloadCount} / {selectedLicense.maxDownloads}
                            </div>
                        </div>
                    </bd.Flex>
                </bd.Paper>

                {/* Broken License Alert */}
                {selectedLicense.isBroken && (
                    <bd.Paper className="mb-3 p-3 bg-danger-subtle border border-danger">
                        <bd.Flex content="between" align="start">
                            <div>
                                <strong><T>broken-license-report</T></strong>
                                <p className="mb-0 mt-2">{selectedLicense.brokenDescription}</p>
                                <small className="text-muted">
                                    <T>reported-at</T>:{' '}
                                    {selectedLicense.brokenReportedAt
                                        ? new Date(selectedLicense.brokenReportedAt).toLocaleString()
                                        : '-'}
                                </small>
                            </div>
                            <bd.Button
                                variant="contained"
                                color="primary"
                                size="sm"
                                onClick={handleResolveBroken}
                                loading={resolveBrokenApi.busy}
                            >
                                <T>resolve</T>
                            </bd.Button>
                        </bd.Flex>
                    </bd.Paper>
                )}

                {/* Status Update */}
                <bd.Paper className="mb-3 p-3">
                    <h6 className="mb-3"><T>update-status</T></h6>
                    <bd.Flex gap={2} wrap>
                        <div style={{ flex: '1 1 400px' }}>
                            <bd.Flex gap={1}>
                                {['Active', 'Expired', 'Suspended', 'Broken'].map(status => (
                                    <bd.Button
                                        key={status}
                                        size="sm"
                                        variant={(newStatus || selectedLicense.status) === status ? 'contained' : 'outline'}
                                        color={
                                            status === 'Suspended' || status === 'Broken'
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        onClick={() => handleUpdateStatus(status)}
                                        loading={updateStatusApi.busy}
                                    >
                                        {status}
                                    </bd.Button>
                                ))}
                            </bd.Flex>
                        </div>
                        <div style={{ flex: '0 0 auto' }}>
                            <bd.Button
                                variant="outline"
                                color="secondary"
                                size="sm"
                                onClick={handleResetDownloadCount}
                                loading={resetDownloadApi.busy}
                            >
                                <T>reset-downloads</T>
                            </bd.Button>
                        </div>
                    </bd.Flex>
                </bd.Paper>

                {/* Download Link */}
                <bd.Paper className="p-3">
                    <h6 className="mb-3"><T>download-link</T></h6>
                    {selectedLicense.downloadLink && (
                        <div className="mb-3 p-2 bg-light rounded">
                            <small className="text-break">{selectedLicense.downloadLink}</small>
                        </div>
                    )}
                    <bd.TextField
                        value={downloadLink}
                        onChange={(e: any) => setDownloadLink(e.target.value)}
                        placeholder={translate('enter-new-download-link')}
                        className="mb-3"
                    />
                    <bd.Button
                        variant="contained"
                        size="sm"
                        icon={<SvgSave />}
                        onClick={handleUpdateLink}
                        loading={updateLinkApi.busy}
                        disabled={!downloadLink.trim()}
                    >
                        <T>update-link</T>
                    </bd.Button>
                </bd.Paper>
            </div>
        </BasicModal>
    );
}
